import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { ButtonBase, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import GuideMessageM from '../components/message/GuideMessageM';
import { PhoneVerify } from '../components/verify';
import { RequesterSettingsDialog } from '../components/requester';
import { CorporateInfoCard } from '../components/corporate';
import { CustomVacantButton } from '../components/button';
import tokens from '../components/theme/tokens';

// 마이페이지 — 회원 정보 · [의뢰자 정보 설정](상호·부서명·주소·상세주소 모달) · 핸드폰 번호 변경 · 관리자(권한 있을 때) · 탈퇴
const JOIN_LABEL = { Email: '이메일', Kakao: '카카오', Naver: '네이버', Google: '구글' };
const TYPE_LABEL = { Private: '개인회원', Company: '기업회원' };

const useStyles = makeStyles((theme) => ({
  title: { marginBottom: 50 },
  box: {
    width: 500,
    maxWidth: '100%',
    padding: '0 30px',
    marginBottom: 36,
  },
  card: {
    width: '100%',
    padding: '22px 24px',
    borderRadius: 16,
    backgroundColor: tokens.surface,
    boxShadow: tokens.cardShadow,
  },
  rows: {
    display: 'grid',
    gridTemplateColumns: '88px 1fr',
    rowGap: 10,
    columnGap: 12,
    fontSize: 15,
    letterSpacing: -0.5,
    '& small': { color: tokens.muted, fontSize: 14 },
    '& b': { color: tokens.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  },
  sub: {
    marginTop: 18,
    paddingTop: 16,
    borderTop: `1px solid ${tokens.lineSoft}`,
  },
  subHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    '& .MuiTypography-root': { fontWeight: 700, color: tokens.ink, fontSize: 15 },
  },
  tag: {
    height: 22,
    padding: '0 8px',
    borderRadius: 11,
    backgroundColor: tokens.primarySoft,
    color: tokens.primaryDark,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '22px',
  },
  settingBtn: { height: 46, fontSize: 15, marginTop: 6 },
  label: { marginBottom: 10 },
  alink: { color: theme.palette.primary.main },
  adminBtn: { height: 46, fontSize: 15 },
}));

function MyPage({ loginStore }) {
  const cs = useStyles();
  const history = useHistory();
  const { userInfo, setPhoneVerified, phoneInitial, phoneCheck, phoneChangeCall, saveRequesterCall, setUserInfo } = loginStore;
  const [reqOpen, setReqOpen] = useState(false);

  useEffect(() => { phoneInitial(); }, [phoneInitial]);

  useEffect(() => {
    if (phoneCheck) {
      phoneChangeCall(() => {
        history.push({ pathname: '/changeresult', state: { subTitle: '핸드폰 번호가 변경되었습니다.' } });
      });
    }
  }, [phoneCheck, phoneChangeCall, history]);

  const r = (userInfo && userInfo.requester) || {};
  const addrText = [r.addr, r.addrDtl].filter(Boolean).join(' ');

  return (
    <React.Fragment>
      <RequesterSettingsDialog open={reqOpen} user={userInfo} onClose={() => setReqOpen(false)} onSave={saveRequesterCall} />
      <Grid className="mainLayout" container direction="column" justify="center" alignItems="center">
        <Grid item className={cs.title}>
          <GuideMessageM title="마이페이지" resultTitle="마이페이지" subTitle="사용자 정보를 수정합니다." />
        </Grid>

        <Grid className={cs.box} container direction="column">
          <div className={cs.card}>
            <div className={cs.rows}>
              <small>이름</small><b>{(userInfo && userInfo.name) || '-'}</b>
              <small>이메일</small><b>{(userInfo && userInfo.email) || '-'}</b>
              <small>연락처</small><b>{(userInfo && userInfo.phone) || '-'}</b>
              <small>회원 구분</small>
              <b>{TYPE_LABEL[userInfo && userInfo.userType] || '개인회원'}{userInfo && userInfo.userJoinType ? ` · ${JOIN_LABEL[userInfo.userJoinType] || userInfo.userJoinType} 가입` : ''}</b>
            </div>
            <div className={cs.sub}>
              <div className={cs.subHead}>
                <Typography>의뢰자 정보</Typography>
                <span className={cs.tag}>{userInfo && userInfo.requesterSaved ? '직접 설정' : '마지막 접수 기준'}</span>
              </div>
              <div className={cs.rows}>
                <small>상호</small><b>{r.company || '-'}</b>
                <small>부서명</small><b>{r.dept || '-'}</b>
                <small>주소</small><b title={addrText}>{addrText || '-'}</b>
              </div>
              <CustomVacantButton className={cs.settingBtn} fullWidth variant="contained" color="primary" onClick={() => setReqOpen(true)}>
                의뢰자 정보 설정
              </CustomVacantButton>
            </div>
          </div>
        </Grid>

        {userInfo && userInfo.userType === 'Company' && (
          <Grid className={cs.box} container direction="column">
            <CorporateInfoCard user={userInfo} onUserChange={setUserInfo} />
          </Grid>
        )}

        <Grid className={cs.box} container direction="column" justify="center" alignItems="flex-start">
          <Typography className={cs.label} variant="body1" color="primary"><b>핸드폰 번호 변경</b></Typography>
          <PhoneVerify purpose="phone" onVerified={(uid) => setPhoneVerified(uid)} />
        </Grid>

        {userInfo && userInfo.role === 'admin' && (
          <Grid className={cs.box} container direction="column">
            <CustomVacantButton className={cs.adminBtn} fullWidth variant="contained" color="primary" onClick={() => history.push('/admin')}>
              관리자 페이지
            </CustomVacantButton>
          </Grid>
        )}

        <Grid item>
          <ButtonBase className={cs.alink} onClick={() => history.push('/leaveId')} focusRipple={false}>
            <Typography variant="body2" color="primary">탈퇴하기</Typography>
          </ButtonBase>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default inject('loginStore')(observer(MyPage));
